import { zodResolver } from '@hookform/resolvers/zod';
import { G } from '@mobily/ts-belt';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeftRounded';
import { Box, IconButton, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  PostFirstConnectionRequest,
  PostForgotPasswordRequest,
  ZodPostFirstConnectionRequest,
  ZodPostFirstConnectionRequestWithPhoneNumber,
} from '@Common/types/bo_external/auth/request_email';

import { TypographyTranslated } from '@sh4res/bo-common/components';
import { CustomLocalesMenuButton } from '@sh4res/bo-common/components/CustomLocalesMenuButton';
import { PageTitle } from '@sh4res/bo-common/components/PageTitle';

import { AxaTypography } from '@Components/AxaTypography';
import { EnterpriseCodeInfoButton } from '@Components/FirstConnection/EnterpriseCodeInfoButton';
import { LoginPageCTA } from '@Components/LoginPageCTA';
import { VerifyEmailPageSuccess } from '@Components/VerifyEmailPageSuccess';
import { TranslationKeys } from '@I18n/types';
import { authClient } from '@Utils/authClient';
import { authProvider } from '@Utils/providers/authProvider';

function useHasPhoneNumberQueryParam() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  return searchParams.has('has_phone_number') && searchParams.get('has_phone_number') === 'true';
}

const ForgotPasswordPageFormWrapper = styled('form')`
  text-align: center;
  display: flex;
  flex-direction: column;
  min-width: 290px;
`;

type Page = 'form' | 'success';
type FormData = PostFirstConnectionRequest | PostForgotPasswordRequest;

export function FirstConnectionPageForm() {
  const translate = useTranslate();
  const notify = useNotify();
  const navigate = useNavigate();
  const testId = 'BoExternalFirstConnectionPageForm';

  const hasPhoneNumber = useHasPhoneNumberQueryParam();

  const onGoBack = () => navigate('/login');

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    getValues,
  } = useForm<FormData>({
    mode: 'onBlur',
    resolver: zodResolver(
      hasPhoneNumber ? ZodPostFirstConnectionRequestWithPhoneNumber : ZodPostFirstConnectionRequest,
    ),
  });

  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<Page>('form');

  const onSubmit = async (formData: FormData) => {
    setLoading(true);

    let success = false;
    try {
      success = await authProvider.activation(formData);
    } catch (error) {
      // eslint-disable-next-line no-console -- expected
      console.log(error);
    }
    if (!success) {
      notify('login_page.messages.forgot_password.failed_activation', { type: 'error' });
      setLoading(false);
      return;
    }

    await authClient.requestPasswordReset(
      {
        email: formData.email,
        redirectTo: `${window.location.origin}/#/auth-callback?type=reset-password`,
      },
      {
        onResponse() {
          setLoading(false);
        },
        onSuccess() {
          setPage('success');
        },
        onError({ error }) {
          // oxlint-disable-next-line no-console
          // eslint-disable-next-line no-console
          console.error(error);
          notify('login_page.messages.forgot_password.failed', { type: 'error' });
        },
      },
    );
  };

  const rawHeaderText = 'login_page.messages.first_connection.header';

  return page === 'success' ? (
    <Box maxWidth={'400px'}>
      <VerifyEmailPageSuccess
        header="login_page.messages.first_connection_success.header"
        subheader="login_page.messages.first_connection_success.subheader"
        subheaderVariables={{ email: getValues().email }}
        note="login_page.messages.first_connection_success.note"
      />
    </Box>
  ) : (
    <>
      <PageTitle title={translate(rawHeaderText)} />
      <IconButton
        data-testid={`${testId}_BackButton`}
        onClick={onGoBack}
        aria-label={translate('contact_support_page.accessibility.back')}
        sx={{
          color: 'black',
          backgroundColor: 'transparent !important;',
          position: 'absolute',
          top: 12,
          left: 12,
        }}>
        <ChevronLeftIcon sx={{ fontSize: '23px' }} />
      </IconButton>
      <Box position="absolute" top={12} right={12}>
        <CustomLocalesMenuButton testId={`${testId}_LocalesMenuButton`} />
      </Box>
      <ForgotPasswordPageFormWrapper onSubmit={handleSubmit(onSubmit)}>
        <TypographyTranslated
          label={rawHeaderText}
          variant="h5"
          component={AxaTypography}
          align="left"
          sx={{ mt: '40px' }}
        />
        <TypographyTranslated
          label={`login_page.messages.first_connection.subheader.${
            hasPhoneNumber ? 'has_phonenumber' : 'has_no_phonenumber'
          }`}
          variant="body2"
          align="left"
          sx={{ mb: '16px', mt: 0 }}
        />
        <TextField
          data-testid={`${testId}_EmailInput`}
          label={translate('login_page.messages.form.email_placeholder')}
          variant="outlined"
          sx={{
            borderRadius: '16px',
            mb: '24px',
            width: '100%',
          }}
          defaultValue=""
          placeholder="abc@def.com"
          InputLabelProps={{ shrink: true }}
          error={!!errors.email}
          helperText={
            G.isNotNullable(errors.email?.message)
              ? translate(errors.email.message as TranslationKeys)
              : undefined
          }
          {...register('email')}
        />

        {hasPhoneNumber ? null : (
          <TextField
            data-testid={`${testId}_EnterpriseCodeInput`}
            label={
              <span>
                {translate('login_page.messages.form.external_code_enterprise_placeholder')}
                <EnterpriseCodeInfoButton testId={`${testId}_EnterpriseCodeInfoButton`} />
              </span>
            }
            variant="outlined"
            sx={{
              borderRadius: '16px',
              mb: '24px',
              width: '100%',
            }}
            defaultValue=""
            placeholder="13749382"
            InputLabelProps={{ shrink: true }}
            error={'code' in errors}
            helperText={
              'code' in errors && G.isNotNullable(errors.code?.message)
                ? translate(errors.code.message as TranslationKeys)
                : null
            }
            {...register('code')}
          />
        )}

        <Stack alignItems="end">
          <LoginPageCTA
            data-testid={`${testId}_SubmitButton`}
            type="submit"
            variant="contained"
            disabled={!isValid || loading}
            sx={{ width: 'max-content' }}>
            <TypographyTranslated
              component={AxaTypography}
              label="login_page.messages.form.cta_text"
            />
          </LoginPageCTA>
        </Stack>
      </ForgotPasswordPageFormWrapper>
    </>
  );
}
